import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UserService } from '../user/user.service';
import { SupportTicketsDialogComponent } from './tickets-dialog.component';
import { UserModel } from '../user/user.model';
import { SupportTicketsService } from './tickets.service';
import { SupportTicketsConversationComponent } from './tickets-conversation.component';
import { NotificationService } from '../../modules_v2/shared/services/notification.service';

export type SupportTicketFilter = 'open' | 'resolved' | 'all';

export interface SupportTicketRow {
    id: number;
    name: string;
    subject: string;
    category: string;
    priority: number;
    resolved: boolean;
    date: string;
}

export const SUPPORT_PRIORITIES = [
    { value: 0, label: 'Baja', className: 'low' },
    { value: 1, label: 'Media', className: 'medium' },
    { value: 2, label: 'Alta', className: 'high' },
];

@Component({
    selector: 'app-supporttickets',
    templateUrl: './tickets.component.html',
    styleUrls: ['./tickets.component.css'],
    standalone: false
})
export class SupportTicketsComponent implements OnInit {

    constructor(
        public dialog: MatDialog,
        private supportTicketsService: SupportTicketsService,
        public userService: UserService,
        private notification: NotificationService,
    ) { }

    loading = true;
    loadError = false;
    currentUser: UserModel;
    tickets: SupportTicketRow[] = [];
    isAdmin = false;
    filter: SupportTicketFilter = 'open';
    search = '';
    resolvingId: number | null = null;

    readonly filters: { value: SupportTicketFilter; label: string }[] = [
        { value: 'open', label: 'Abiertas' },
        { value: 'resolved', label: 'Resueltas' },
        { value: 'all', label: 'Todas' },
    ];

    get openCount(): number {
        return this.tickets.filter(t => !t.resolved).length;
    }

    get resolvedCount(): number {
        return this.tickets.filter(t => t.resolved).length;
    }

    get visibleTickets(): SupportTicketRow[] {
        const term = this.search.trim().toLowerCase();
        return this.tickets.filter(t => {
            if (this.filter === 'open' && t.resolved) return false;
            if (this.filter === 'resolved' && !t.resolved) return false;
            if (!term) return true;
            return [t.subject, t.category, t.name].some(v => (v || '').toLowerCase().includes(term));
        });
    }

    ngOnInit() {
        this.userService.loadData().then(userData => {
            this.currentUser = userData;
            this.isAdmin = !!userData?.isAdmin;
        });
        this.load();
    }

    load(): void {
        this.loading = true;
        this.loadError = false;
        this.supportTicketsService.getListTickets()
            .then(response => {
                this.tickets = ((response || []) as unknown as SupportTicketRow[])
                    .slice()
                    .sort((a, b) => b.id - a.id);
            })
            .catch(() => { this.loadError = true; })
            .finally(() => { this.loading = false; });
    }

    priority(value: number) {
        return SUPPORT_PRIORITIES.find(p => p.value === value) || SUPPORT_PRIORITIES[0];
    }

    viewTicket(ticket: SupportTicketRow) {
        this.dialog.open(SupportTicketsConversationComponent, {
            width: '680px',
            maxWidth: '96vw',
            maxHeight: '90vh',
            autoFocus: false,
            data: { ticketId: ticket.id }
        }).afterClosed().subscribe(changed => { if (changed) this.load(); });
    }

    resolveTicket(ticket: SupportTicketRow) {
        if (this.resolvingId) return;
        this.resolvingId = ticket.id;
        this.supportTicketsService.resolveTicket(ticket.id)
            .then(() => {
                this.notification.ok('Incidencia marcada como resuelta');
                this.load();
            })
            .catch(() => this.notification.error('No se pudo resolver la incidencia'))
            .finally(() => { this.resolvingId = null; });
    }

    newTicket() {
        this.dialog.open(SupportTicketsDialogComponent, {
            width: '560px',
            maxWidth: '96vw',
            maxHeight: '90vh',
            autoFocus: false,
        }).afterClosed().subscribe(created => {
            if (created) {
                this.filter = 'open';
                this.load();
            }
        });
    }

    public trackById(_: number, ticket: SupportTicketRow) { return ticket.id; }
}
